import type { IconName } from '@pypestream/design-system';
import { ResourceLibItem } from '../../../xstate/prostudio.xstate.types';

export interface MicroagentCategory {
  id: string;
  name: string;
  icon: IconName;
}

export const UNCATEGORIZED_ID = 'uncategorized';

export const MICROAGENT_CATEGORIES: MicroagentCategory[] = [
  { id: 'user-input', name: 'Processing user input', icon: 'input-pipe' },
  { id: 'variables', name: 'Working with variables', icon: 'brackets' },
  { id: 'menu-nav', name: 'Fancy menus & navigation', icon: 'layout-top' },
  { id: 'datetime', name: 'Working with date & time', icon: 'calendar' },
  { id: 'agent-escalation', name: 'Agent escalation logic', icon: 'comments' },
  { id: 'google-maps', name: 'Google Maps integration', icon: 'location-dot' },
  {
    id: 'api-integrations',
    name: 'API integrations and external systems',
    icon: 'database',
  },
  { id: 'nlu', name: 'Free text & NLU', icon: 'stars' },
  { id: 'tools', name: 'Meta data & frontend tools', icon: 'data-script' },
  { id: 'custom-ui', name: 'Custom UI embeds', icon: 'layout-code' },
  {
    id: 'external-media',
    name: 'External media/communication',
    icon: 'modem',
  },
  { id: 'feedback', name: 'Feedback surveys', icon: 'face-smile' },
];

/** Items whose name is not in the map — listed, never hidden. */
/** Ids of the library items that are agent templates. */
export const AGENT_TEMPLATE_ITEM_PREFIX = 'agent-template:';

/** Project Bobcat (agent templates): whole agents, listed first,
 *  only when there are any (`agent-template-items.ts`). */
export const AGENT_TEMPLATES_CATEGORY: MicroagentCategory = {
  id: 'agent-templates',
  name: 'Agent templates',
  icon: 'microagent',
};

export const UNCATEGORIZED_CATEGORY: MicroagentCategory = {
  id: UNCATEGORIZED_ID,
  name: 'Uncategorized',
  icon: 'microagent',
};

/**
 * The real `studioResources` catalog has no category field (the backend type
 * is just {id, type, name, nodes, description, data}), so categories are a
 * client-side lookup by name. Anything not listed falls back to
 * `UNCATEGORIZED_ID` rather than being hidden, so a new catalog entry is never
 * silently dropped.
 *
 * Checked live on 2026-09-24: the real catalog holds ONE microagent, "Email
 * validation". The other entries are the microagent names the Protolab
 * build-flow prototype curated from 11 real solutions
 * (`LIBRARY_CATEGORIES`), so those land in the right category the moment the
 * catalog carries them. Keep this in sync as the real catalog grows.
 */
const NAME_TO_CATEGORY_ID: Record<string, string> = {
  // Real catalog.
  'Email validation': 'user-input',
  // Curated in the prototype.
  'Regex validation and detection': 'user-input',
  'Phone validation & formatting': 'user-input',
  'Address validation (Google)': 'user-input',
  'Document OCR intake': 'user-input',
  'File upload': 'user-input',
  'Field collection & retry loop': 'variables',
  'Storing a button or listpicker selection': 'menu-nav',
  'Menu / multi-match selection routing': 'menu-nav',
  'Persistent menu setup': 'menu-nav',
  'Date validation': 'datetime',
  'Date & time picker': 'datetime',
  'Agent availability & wait time check': 'agent-escalation',
  'Zip code lookup': 'google-maps',
  'External API call': 'api-integrations',
  'Searching for trigger words or phrases': 'nlu',
  'Supervisor routing loop': 'nlu',
  'Webview height scaling': 'tools',
  'Send SMS notification': 'external-media',
  'Star rating setup': 'feedback',
};

export const categoryIdOf = (
  item: Pick<ResourceLibItem, 'name'> & Partial<Pick<ResourceLibItem, 'id'>>
): string =>
  item.id?.startsWith(AGENT_TEMPLATE_ITEM_PREFIX)
    ? AGENT_TEMPLATES_CATEGORY.id
    : (NAME_TO_CATEGORY_ID[item.name ?? ''] ?? UNCATEGORIZED_ID);
